import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ACCOUNTING_API } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';

interface CashboxTransactionLog {
  id: number;
  companyId: string;
  transactionId: number;
  cashboxId: number;
  type: string;
  transactionType?: string;
  amount: number;
  description?: string;
  currency: string;
  paymentMethod?: string;
  balanceBefore: number;
  balanceAfter: number;
  referenceId?: string;
  referenceType?: string;
  accountId?: number;
  accountName?: string;
  screenId?: string;
  screenName?: string;
  date: string;
  documentNo?: string;
  changedField: string;
  reason?: string;
  deletedBy?: string;
  deletedAt: string;
  originalCreatedBy?: string;
  originalCreatedByEmail?: string;
  originalCreatedAt: string;
  isActive: boolean;
}

interface CashboxTransactionLogsModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string | null;
}

// Para birimi sembolünü döndür
const getCurrencySymbol = (currency: string): string => {
  switch (currency) {
    case 'TRY': return '₺';
    case 'USD': return '$';
    case 'EUR': return '€';
    case 'GBP': return '£';
    case 'JPY': return '¥';
    case 'CHF': return 'Fr.';
    case 'AUD': return 'A$';
    case 'CAD': return 'C$';
    case 'CNY': return '¥';
    default: return currency;
  }
};

const CashboxTransactionLogsModal: React.FC<CashboxTransactionLogsModalProps> = ({ open, onClose, companyId }) => {
  const { t, i18n } = useTranslation('accounting');
  const locale = i18n.language?.toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR';
  const [logs, setLogs] = useState<CashboxTransactionLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getTransactionTypeLabel = (type?: string): string => {
    if (!type) return '-';
    const keyMap: Record<string, string> = {
      DEPOSIT: 'deposit',
      WITHDRAWAL: 'withdrawal',
      WITHDRAW: 'withdrawal',
      TRANSFER: 'transfer',
      QUICK_SALE: 'quickSale',
      COLLECTION: 'collection',
      PAYMENT: 'payment',
      EXPENSE: 'expense',
      LOAN_DISBURSEMENT: 'loanDisbursement',
      LOAN_INSTALLMENT_PAYMENT: 'loanInstallmentPayment',
    };
    const key = keyMap[type.toUpperCase()];
    return key ? t(`cash.transactionReport.transactionTypes.${key}`) : type;
  };

  // Modal açıldığında logları yükle
  useEffect(() => {
    if (open && companyId) {
      loadLogs();
    }
  }, [open, companyId]);

  const loadLogs = async () => {
    if (!companyId) {
      setError(t('cash.transactionReport.errors.companyNotFound'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('📋 Kasa işlem logları yükleniyor...');

      const response = await accountingApi.get(ACCOUNTING_API.CASHBOX_TRANSACTION_LOGS_BY_COMPANY);
      const logsData = response.data.data || response.data;

      console.log('✅ Loglar yüklendi:', logsData);
      setLogs(logsData);
    } catch (error: any) {
      console.error('❌ Loglar yüklenirken hata:', error);
      setError(error.message || t('cash.logsModal.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Tarih formatla
  const formatDate = (dateString: string): string => {
    try {
      const date = parseISO(dateString);
      return format(date, 'dd.MM.yyyy HH:mm:ss');
    } catch {
      return dateString;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
    >
      <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">{t('cash.logsModal.title')}</Typography>
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          aria-label={t('cash.common.close')}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>{t('cash.logsModal.loading')}</Typography>
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : logs.length === 0 ? (
          <Alert severity="info">{t('cash.logsModal.empty')}</Alert>
        ) : (
          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                    {t('cash.logsModal.columns.deletedAt')}
                  </TableCell>
                  <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                    {t('cash.logsModal.columns.deletedBy')}
                  </TableCell>
                  <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                    {t('cash.logsModal.columns.transactionDate')}
                  </TableCell>
                  <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                    {t('cash.logsModal.columns.cashbox')}
                  </TableCell>
                  <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                    {t('cash.logsModal.columns.transactionType')}
                  </TableCell>
                  <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                    {t('cash.logsModal.columns.amount')}
                  </TableCell>
                  <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                    {t('cash.logsModal.columns.description')}
                  </TableCell>
                  <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                    {t('cash.logsModal.columns.reason')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {formatDate(log.deletedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{log.deletedBy || '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(log.date)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {t('cash.logsModal.cashboxPrefix')} #{log.cashboxId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getTransactionTypeLabel(log.transactionType)}
                        size="small"
                        color={log.type === 'INCOME' ? 'success' : 'error'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color={log.type === 'INCOME' ? 'success.main' : 'error.main'}
                      >
                        {log.type === 'INCOME' ? '+' : '-'}{log.amount.toLocaleString(locale)} {getCurrencySymbol(log.currency)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {log.description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {log.reason || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CashboxTransactionLogsModal;

